import { Popover } from "@circleco/compass/components/Popover";
import { Typography } from "@circleco/compass/components/Typography";
import type { UIEvent } from "react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { SuggestionMenuRow } from "./SuggestionMenuRow";
import type {
  SuggestionGroup,
  SuggestionItem,
  SuggestionMenuHandle,
} from "./types";

const END_REACHED_THRESHOLD_PX = 40;

const wrapIndex = (index: number, length: number) =>
  length === 0 ? 0 : (index + length) % length;

export interface SuggestionMenuProps {
  anchorContextElement?: Element | null;
  getAnchorRect: () => DOMRect | null;
  groups: SuggestionGroup[];
  hasMore?: boolean;
  onClose?: () => void;
  onEndReached?: () => void;
  onSelect: (item: SuggestionItem) => void;
  open: boolean;
  resetKey?: string;
  side?: "bottom" | "top";
}

export const SuggestionMenu = forwardRef<
  SuggestionMenuHandle,
  SuggestionMenuProps
>(
  (
    {
      anchorContextElement,
      getAnchorRect,
      groups,
      hasMore,
      onClose,
      onEndReached,
      onSelect,
      open,
      resetKey,
      side = "bottom",
    },
    ref,
  ) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const activeItemRef = useRef<HTMLButtonElement | null>(null);

    const items = groups.flatMap(group => group.items);
    const itemsRef = useRef(items);
    itemsRef.current = items;

    const resolvedResetKey = resetKey ?? items.map(item => item.id).join("|");

    useEffect(() => {
      setActiveIndex(0);
    }, [resolvedResetKey]);

    useEffect(() => {
      activeItemRef.current?.scrollIntoView({ block: "nearest" });
    }, [activeIndex, resolvedResetKey]);

    useImperativeHandle(ref, () => ({
      moveDown: () => {
        const length = itemsRef.current.length;
        const isAtEnd = length > 0 && activeIndex >= length - 1;
        if (isAtEnd && hasMore) {
          onEndReached?.();
          return;
        }
        setActiveIndex(index => wrapIndex(index + 1, itemsRef.current.length));
      },
      moveUp: () =>
        setActiveIndex(index => wrapIndex(index - 1, itemsRef.current.length)),
      selectActive: () => {
        const item = itemsRef.current[activeIndex];
        if (!item) return false;
        onSelect(item);
        return true;
      },
    }));

    if (!open || items.length === 0) {
      return null;
    }

    let groupStartIndex = 0;
    const indexedGroups = groups.map(group => {
      const rows = group.items.map((item, indexInGroup) => ({
        item,
        index: groupStartIndex + indexInGroup,
      }));
      groupStartIndex += group.items.length;
      return { ...group, rows };
    });

    const handleScroll = (event: UIEvent<HTMLDivElement>) => {
      if (!onEndReached) return;
      const { clientHeight, scrollHeight, scrollTop } = event.currentTarget;
      if (scrollHeight - scrollTop - clientHeight < END_REACHED_THRESHOLD_PX) {
        onEndReached();
      }
    };

    return (
      <Popover
        open
        align="start"
        side={side}
        sideOffset={4}
        anchor={{
          getBoundingClientRect: () => getAnchorRect() ?? new DOMRect(),
          contextElement: anchorContextElement ?? undefined,
        }}
        onOpenChange={isOpen => {
          if (!isOpen) onClose?.();
        }}
        className="!p-1"
      >
        <div
          className="max-h-72 w-64 overflow-y-auto"
          data-suggestion-menu=""
          data-testid="suggestion-menu"
          onScroll={handleScroll}
        >
          {indexedGroups.map(group => (
            <div key={group.id}>
              {group.label && (
                <div className="px-2 pb-1 pt-2">
                  <Typography variant="label-xs" color="secondary">
                    {group.label}
                  </Typography>
                </div>
              )}
              {group.rows.map(({ item, index }) => (
                <SuggestionMenuRow
                  key={item.id}
                  item={item}
                  isActive={index === activeIndex}
                  rowRef={index === activeIndex ? activeItemRef : undefined}
                  onHover={() => setActiveIndex(index)}
                  onSelect={() => onSelect(item)}
                />
              ))}
            </div>
          ))}
        </div>
      </Popover>
    );
  },
);

SuggestionMenu.displayName = "SuggestionMenu";
